import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, mintaIsiPratayang, statusIsiPratayang, type StatusIsiPratayang } from '../lib/api';
import { useBahasa } from '../i18n';

/**
 * Tombol "isi pratayang yang bolong" (§D8.1, 8 September 2026).
 *
 * Diminta lapangan sesudah backfill latar terbukti terlalu pelan untuk arsip
 * yang belum pernah punya sprite sama sekali: *"ini udah oke, cuma
 * thumbnails-nya masih bolong bolong. Di setiap halaman camera, tolong ada
 * tombol 'fill blank thumbnails' atau semacamnya."*
 *
 * Satu komponen untuk dua tempat — halaman Playback satu kamera dan halaman
 * Settings untuk seluruh armada — karena seluruh bagian yang rumit di sini
 * sama: menekan tombol, menahan diri supaya tidak ditekan dua kali, mem-poll
 * kemajuan, dan berhenti mem-poll begitu selesai. Dua salinan logika itu adalah
 * dua tempat yang bisa lupa berhenti.
 *
 * **Yang sudah ada tidak pernah dibuat ulang**, dan itu ditegakkan worker, bukan
 * di sini — penilainya keberadaan berkas sprite-nya sendiri. Tombol ini cuma
 * mengubah urutan: kamera yang sedang dilihat orang naik ke depan antrean.
 */

/** Sesering apa kemajuan ditanyakan selama pengisian berjalan. */
const POLL_MS = 2_000;

export function TombolIsiPratayang({
  cameraId,
  label,
}: {
  /** `null` = seluruh armada. */
  cameraId: string | null;
  label: string;
}) {
  const { t } = useBahasa();
  const [status, setStatus] = useState<StatusIsiPratayang | null>(null);
  const [sibuk, setSibuk] = useState(false);
  const [galat, setGalat] = useState<string | null>(null);

  /**
   * Status yang sedang berjalan mungkin **milik permintaan orang lain**, atau
   * milik kamera lain. Yang ditampilkan tombol ini karena itu disaring: kemajuan
   * kamera sebelah tidak boleh terlihat seperti kemajuan kamera ini.
   */
  const milikKita = status !== null && status.cameraId === cameraId;

  const segarkan = useCallback(async () => {
    try {
      setStatus(await statusIsiPratayang());
    } catch {
      // Diam: gagal membaca kemajuan bukan alasan menakut-nakuti orang tentang
      // pekerjaan yang mungkin berjalan baik-baik saja.
    }
  }, []);

  useEffect(() => {
    void segarkan();
  }, [segarkan]);

  // Poll hanya selama ada yang berjalan — halaman Playback dibuka sepanjang
  // hari, dan polling tetap untuk jawaban yang hampir selalu "tidak ada" adalah
  // beban tetap tanpa imbalan.
  const jalan = status?.aktif === true;
  const segarRef = useRef(segarkan);
  segarRef.current = segarkan;
  useEffect(() => {
    if (!jalan) return;
    const timer = setInterval(() => void segarRef.current(), POLL_MS);
    return () => clearInterval(timer);
  }, [jalan]);

  const mulai = async () => {
    setSibuk(true);
    setGalat(null);
    try {
      await mintaIsiPratayang(cameraId);
      // Satu pembacaan segera supaya tombolnya langsung berubah jadi
      // "sedang berjalan" — menunggu poll berikutnya membuat dua detik pertama
      // terasa seperti tombol yang tidak tertekan.
      await segarkan();
    } catch (e) {
      setGalat(e instanceof ApiError ? e.message : (e as Error).message);
    } finally {
      setSibuk(false);
    }
  };

  const persen =
    milikKita && status.total > 0
      ? Math.min(100, Math.round((status.diperiksa / status.total) * 100))
      : 0;

  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
      <button
        type="button"
        onClick={() => void mulai()}
        disabled={sibuk || jalan}
        className="flex min-h-tap items-center gap-1.5 rounded-md border border-line px-3 hover:bg-subtle disabled:opacity-50"
      >
        <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <rect x="3" y="4" width="18" height="13" rx="2" />
          <path d="m3 14 4-4 4 4 3-3 7 6" />
        </svg>
        {label}
      </button>

      {jalan && !milikKita && (
        <span className="text-muted">{t('Another fill is already running.')}</span>
      )}

      {milikKita && status.aktif && (
        <span className="flex items-center gap-2 tabular-nums text-muted">
          {/* Dua angka, bukan satu: "diperiksa dari total" menjawab *berapa lama
              lagi*, "dibuat" menjawab *apakah ada gunanya*. Arsip yang ternyata
              sudah lengkap akan berjalan sampai 100% dengan dibuat 0, dan itu
              jawaban yang benar — bukan tanda ada yang salah. */}
          <span
            className="h-1.5 w-24 overflow-hidden rounded-full bg-line"
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={persen}
          >
            <span className="block h-full bg-accent transition-[width]" style={{ width: `${persen}%` }} />
          </span>
          {t('{n}% · {dibuat} created', { n: persen, dibuat: status.dibuat })}
        </span>
      )}

      {milikKita && !status.aktif && (
        <span className="text-muted">
          {t('Done — {dibuat} previews created, {dilewati} already had one.', {
            dibuat: status.dibuat,
            dilewati: status.dilewati,
          })}
        </span>
      )}

      {galat && (
        <span className="text-amber-500" role="alert">
          {galat}
        </span>
      )}
    </div>
  );
}
